<script setup lang="ts">

import {computed, ref} from "vue";
import useBackend from "@/composition/useBackend";
import {formatting} from "@hantera/portal-app";
import PeriodEditor from "./PeriodEditor.vue";
import {Component, Rule} from "./rule-types";

const backend = useBackend();

const components = ref<Component[]>([]);
const rules = ref<Rule[]>([]);

const state = ref<{
  type: 'list'
} | {
  type: 'new'
} | {
  type: 'edit'
  rule: Rule
}>({ type: 'list' });

const activeCampaigns = computed(() => {
  return rules.value.filter(r => new Date(r.activeFrom) < new Date() && new Date() < new Date(r.activeTo));
})

const upcomingCampaigns = computed(() => {
  return rules.value.filter(r => new Date() < new Date(r.activeFrom));
})

const pastCampaigns = computed(() => {
  return rules.value.filter(r => new Date(r.activeTo) < new Date());
})

await reload();

async function reload() {
  const loadedComponents = await loadComponents();
  const loadedRules = await loadRules();
  
  components.value = loadedComponents;
  rules.value = loadedRules;
}

async function loadRules(): Promise<Rule[]> {
  // TODO Add /promotion-periods path
  const response = await backend.fetch('/core/resources/rules/promotion-periods/?searchType=prefix')

  if (!response.ok) {
    // TODO Display error
    return [];
  }

  return await response.json()
}

async function loadComponents(): Promise<Component[]> {
  const response = await backend.fetch('/core/resources/components/promotions/?searchType=prefix')

  if (!response.ok) {
    // TODO Display error
    return [] as any;
  }

  return (await response.json() as Component[]).filter(r => r.runtime === 'rule')
}

function edit(rule: Rule) {
  state.value = {
    type: 'edit',
    rule: rule
  }
}

function createNew() {
  state.value = {
    type: 'new'
  }
}

async function showList() {
  
  await reload();
  
  state.value = {
    type: 'list'
  }
}


function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  
  if (date.getFullYear() === 1 || date.getFullYear() > 2099) {
    return "-"
  }
  
  return formatting.dateTime(date);
}

</script>

<template>
  
  <div class="margin">
    <template v-if="state.type === 'list'">
    
      <h2>Promotion Periods</h2>
      
      <div class="primary-actions">
        <button class="btn-primary" @click="createNew">Create new</button>
      </div>

      <div class="box-content-label">
        Active
      </div>
      <div class="box-content">
        
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>From</th>
              <th>To</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="rule in activeCampaigns"
              :key="rule.ruleId"
            >
              <td><button class="btn-link" @click.prevent.stop="edit(rule)">{{rule.displayName}}</button></td>
              <td>{{formatDate(rule.activeFrom)}}</td>
              <td>{{formatDate(rule.activeTo)}}</td>
            </tr>
          </tbody>
        </table>
      </div>
  
      <div class="box-content-label">
        Upcoming
      </div>

      <div class="box-content">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>From</th>
              <th>To</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="rule in upcomingCampaigns"
              :key="rule.ruleId"
            >
              <td><button class="btn-link" @click.prevent.stop="edit(rule)">{{rule.displayName}}</button></td>
              <td>{{formatDate(rule.activeFrom)}}</td>
              <td>{{formatDate(rule.activeTo)}}</td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <div class="box-content-label">
        Past
      </div>

      <div class="box-content">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>From</th>
              <th>To</th>
            </tr>
          </thead>
          <tbody>
          <tr
            v-for="rule in pastCampaigns"
            :key="rule.ruleId"
          >
            <td><button class="btn-link" @click.prevent.stop="edit(rule)">{{rule.displayName}}</button></td>
            <td>{{formatDate(rule.activeFrom)}}</td>
            <td>{{formatDate(rule.activeTo)}}</td>
          </tr>
          </tbody>
        </table>
      </div>

    </template>

    <template v-else-if="state.type === 'new'">
      <h2>New Promotion Period</h2>
      
      <PeriodEditor
        :components="components"
        @saved="showList"
        @cancelled="showList"
      />
    </template>
    
    <template v-else-if="state.type === 'edit'">
      <h2>Edit Promotion Period</h2>

      <PeriodEditor
        :components="components"
        @saved="showList"
        @cancelled="showList"
        @deleted="showList"
        :rule="(state as any).rule"
      />
    </template>
  </div>
  
  
</template>

<style scoped>

h2 {
  margin-bottom: 5px;
}

.primary-actions {
  display: flex;
  gap: 5px;
  margin-bottom: 10px;
  padding: 5px;
}

.box-content {
  max-width: 600px;
}

td, th {
  padding: 3px;
}

.margin {
  margin: 10px;
}

</style>